import { useChat } from '@ai-sdk/react';
import AIChatMessages from '@cobliteam/ai/src/components/AIChatMessages';
import AIIcon from '@cobliteam/ai/src/components/AIIcon';
import { chatTransport } from '@cobliteam/ai/src/lib/transporters';
import { AGENTS } from '@cobliteam/ai/src/types';
import useUser from '@cobliteam/hooks/src/useEntity/useUser';
import Button from '@cobliteam/ui/components/Button';
import Typography from '@cobliteam/ui/components/Typography';
import Icon from '@cobliteam/ui/icons';
import { memo, useEffect, useMemo, useRef } from 'react';
import AgentChatInput from './AgentChatInput';

interface AgentChatPanelProps {
  onClose: () => void;
  initialMessage?: string;
  threadId: string;
}

const AgentChatPanel = ({ onClose, initialMessage, threadId }: AgentChatPanelProps) => {
  const { getUser, getSession } = useUser();
  const user = getUser();
  const session = getSession();

  const transport = useMemo(() => {
    const userId = user?.id ?? 'unknownUser';
    const fleetId = session?.fleet?.id ?? 'unknownFleet';
    const resourceId = `scheduling_${userId}_${fleetId}` as const;
    return chatTransport({
      agentName: AGENTS.agentScheduling,
      threadId,
      resourceId,
      source: 'web',
      component: 'floating',
      url: window.location.href,
    });
  }, [threadId, user?.id, session?.fleet?.id]);

  const {
    messages: rawMessages,
    status,
    sendMessage,
    stop,
    regenerate,
  } = useChat({
    id: threadId,
    transport,
    experimental_throttle: 100,
  });

  const messages = useMemo(() => rawMessages.map((msg) => ({ ...msg, parts: msg.parts.filter((p) => p.type === 'text') })), [rawMessages]);

  const initialMessageSentRef = useRef(false);

  useEffect(() => {
    if (initialMessage && status === 'ready' && !initialMessageSentRef.current) {
      initialMessageSentRef.current = true;
      void sendMessage({
        role: 'user',
        id: crypto.randomUUID(),
        parts: [{ type: 'text', text: initialMessage }],
      }).catch(() => {
        initialMessageSentRef.current = false;
      });
    }
  }, [initialMessage, status, sendMessage]);

  return (
    <div
      data-testid='agent-chat-panel'
      className='fixed right-4 bottom-24 z-[1002] flex h-[min(600px,calc(100vh-8rem))] w-[min(440px,calc(100vw-2rem))] flex-col overflow-hidden rounded-xl border border-gray-4 bg-white shadow-2xl'
    >
      <div className='flex items-center justify-between border-gray-4 border-b px-4 py-3'>
        <div className='flex items-center gap-2'>
          <AIIcon size={16} />
          <Typography variant='description-title'>Agente de Agendamento</Typography>
        </div>
        <Button variant='ghost' size='sm' icon={<Icon name='x' size='sm' />} onClick={onClose} aria-label='Fechar chat' />
      </div>

      <div className='min-h-0 flex-1 overflow-y-auto'>
        {messages.length === 0 ? (
          <div className='flex h-full flex-col items-center justify-center gap-2 p-6 text-center'>
            <AIIcon size={32} />
            <Typography variant='description-title'>Como posso ajudar?</Typography>
            <Typography variant='description-list-default' color='secondary'>
              Sou o agente de agendamento. Posso ajudar a agendar instalações de dispositivos nos veículos da sua frota.
            </Typography>
          </div>
        ) : (
          <AIChatMessages messages={messages} status={status} onRegenerate={regenerate} classNameContent='p-4' />
        )}
      </div>

      <AgentChatInput sendMessage={sendMessage} status={status} stop={stop} />
    </div>
  );
};

export default memo(AgentChatPanel);
